import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocaleDateTime } from "@/utils/formatUtils";
import { sortableColumnProps } from "@/utils/tableUtils";
import { isDefined, numberOrZero, toDate } from "@/utils/utils";
import { Card, Divider, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import {
  useDownloadContractsExport,
  useFilterContracts,
  useToggleVerificationExportInclusion,
} from "../../../../../common/api/queries";
import { ExportFileType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { formatAgentName } from "../../../../agent/utils";
import ComponentWithPermission from "../../../../auth/components/ComponentWithPermission";
import { useCurrentAgentAccount } from "../../../../auth/hooks";
import { ClientTypeTagWithName } from "../../../../client/components/tags/ClientTypeTagWithName";
import { ContractOrderByColumn, ContractVerificationStatus, ContractView } from "../../../enums";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { ContractFilterPageRequest, ContractList } from "../../../types";
import {
  canSendContractToBackoffice,
  handleTableChange,
  hasVerificationExportTogglePermissions,
  VERIFICATION_STATUSES_FOR_SENDING_TO_BACKOFFICE,
} from "../../../utils";
import ContractAmountInfoPopover from "../../ContractAmountInfoPopover";
import ContractChangeVerificationStatusForm from "../../forms/ContractChangeVerificationStatusForm";
import { ContractOriginTag } from "../../tags/ContractOriginTag";
import ContractStatusTag from "../../tags/ContractStatusTag";
import ContractVerificationStatusTag from "../../tags/ContractVerificationStatusTag";

type Props = {
  validatedSearchParams?: ContractFilterPageRequest;
  updateSearchParams: (searchParams: ContractFilterPageRequest, append?: boolean, replace?: boolean) => void;
};

export const ContractListTableView = ({ validatedSearchParams, updateSearchParams }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();

  const [contractForVerificationForm, setContractForVerificationForm] = useState<ContractList>();

  const { data: contractListData, isFetching } = useFilterContracts(validatedSearchParams);
  const toggleVerificationExportInclusion = useToggleVerificationExportInclusion();
  const downloadContractsExport = useDownloadContractsExport();

  const columns = useMemo(() => {
    const items: ColumnsType<ContractList> = [
      {
        key: "createdAt",
        title: t("contract.attrs.createdAt"),
        width: 170,
        dataIndex: ContractOrderByColumn.CREATED_AT,
        ...sortableColumnProps(ContractOrderByColumn.CREATED_AT, contractListData),
        render: (_, record) => (record.createdAt ? formatLocaleDateTime(record.createdAt) : undefined),
      },
      {
        key: "effectiveBeginningOrSignDate",
        title: (
          <>
            {t("contract.attrs.effectiveBeginningDateShort")}
            <br />
            {t("contract.attrs.signDate")}
          </>
        ),
        width: 150,
        dataIndex: ContractOrderByColumn.EFFECTIVE_BEGINNING_OR_SIGN_DATE,
        ...sortableColumnProps(ContractOrderByColumn.EFFECTIVE_BEGINNING_OR_SIGN_DATE, contractListData),
        render: (_, record) => {
          const date = record.effectiveBeginningDate || record.signDate;
          return date ? formatLocaleDate(date) : undefined;
        },
      },
      {
        key: "verifiedAt",
        title: t("contract.attrs.verifiedAt"),
        width: 150,
        ...sortableColumnProps(ContractOrderByColumn.VERIFIED_AT, contractListData),
        render: (_, record) => (record.verifiedAt ? formatLocaleDate(record.verifiedAt) : undefined),
      },
      {
        key: "contractNumbers",
        title: t("contract.attrs.contractNumber"),
        width: 160,
        render: (_, record) => (
          <>
            {record.contractNumber && (
              <>
                <Ellipsis tooltip={record.contractNumber}>
                  <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                    {record.contractNumber}
                  </Link>
                </Ellipsis>
                <br />
              </>
            )}
            {record.secondaryContractNumber && (
              <Ellipsis tooltip={record.secondaryContractNumber}>
                <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                  {record.secondaryContractNumber}
                </Link>
              </Ellipsis>
            )}
          </>
        ),
      },
      {
        key: "client",
        title: t("contract.attrs.client"),
        width: 230,
        render: (_, record) =>
          record.policyHolder && <ClientTypeTagWithName client={record.policyHolder} ellipsis showLink />,
      },
      {
        key: "institution",
        title: t("contract.attrs.institution"),
        width: 170,
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "product",
        title: t("contract.attrs.product"),
        width: 210,
        render: (_, record) => <Ellipsis>{record.product.name}</Ellipsis>,
      },
      {
        key: "gainerRecords",
        title: t("contract.attrs.gainerRecords._label"),
        width: 240,
        render: (_, record) => {
          const today = dayjs();

          const gainerRecord =
            record.gainerRecords?.length === 1 || today.isSameOrBefore(toDate(record.gainerRecords?.[0]?.startDate))
              ? record.gainerRecords?.[0]
              : record.gainerRecords?.find(
                  (gainerRecord) =>
                    toDate(gainerRecord.startDate)?.isSameOrBefore(today, "day") &&
                    (!gainerRecord.endDate || toDate(gainerRecord.endDate)?.isSameOrAfter(today, "day")),
                );
          return (
            <Ellipsis>
              {[
                gainerRecord?.gainer1,
                gainerRecord?.gainer2,
                gainerRecord?.gainer3,
                gainerRecord?.gainer4,
                gainerRecord?.gainer5,
              ]
                .filter((gainer) => !!gainer)
                .map((gainer) => formatAgentName(gainer, false))
                .join(", ")}
            </Ellipsis>
          );
        },
      },
      {
        key: "annualPremiumOrApprovedAmount",
        title: <ContractAmountInfoPopover />,
        width: 130,
        align: "right",
        dataIndex: ContractOrderByColumn.ANNUAL_PREMIUM_OR_APPROVED_AMOUNT,
        ...sortableColumnProps(ContractOrderByColumn.ANNUAL_PREMIUM_OR_APPROVED_AMOUNT, contractListData),
        render: (_, record) => {
          const amount = record.annualPremium || record.approvedAmount || record.targetAmount || record.tradeAmount;
          return isDefined(amount) ? formatLocaleCurrency(amount) : undefined;
        },
      },
      {
        key: "status",
        title: (
          <>
            {t("contract.enums.status._label")}
            <br />
            {t("contract.enums.verificationStatus._label")}
          </>
        ),
        width: 200,
        align: "center",
        render: (_, record) =>
          record.status && record.verificationStatus ? (
            <>
              <ContractStatusTag status={record.status} style={{ marginRight: 0 }} />
              <br />
              <ContractVerificationStatusTag
                status={record.verificationStatus}
                note={record.verificationNote}
                includedInExport={record.includeInVerificationExport}
                style={{ marginTop: 6, marginRight: 0 }}
                onSendClick={
                  VERIFICATION_STATUSES_FOR_SENDING_TO_BACKOFFICE.includes(record.verificationStatus) &&
                  userAccount &&
                  canSendContractToBackoffice(userAccount, record)
                    ? () => setContractForVerificationForm(record)
                    : undefined
                }
                onExportToggleClick={
                  record.verificationStatus === ContractVerificationStatus.SENT &&
                  userAccount &&
                  hasVerificationExportTogglePermissions(record.type, userAccount.permissions)
                    ? () =>
                        toggleVerificationExportInclusion.mutate({
                          id: record.id,
                          object: { optimisticLockVersion: record.optimisticLockVersion as number },
                        })
                    : undefined
                }
              />
            </>
          ) : undefined,
      },
      {
        key: "origin",
        align: "center",
        fixed: "right",
        width: 50,
        render: (_, record) => record.origin && <ContractOriginTag origin={record.origin} />,
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) => (
          <ActionTextIcon
            path={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}
            icon="eye"
            color="blue"
            text={t("common.show")}
          />
        ),
      },
    ];

    return items;
  }, [contractListData, userAccount]);

  return (
    <Card className="card-box">
      <ComponentWithPermission permissions={[Permission.CONTRACT_EXPORT]}>
        <div className={contractListData?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
          <ActionTextIcon
            icon="download"
            color="blue"
            text={t("common.exportXlsx")}
            onClick={() =>
              downloadContractsExport.mutate({
                ...validatedSearchParams,
                exportFileType: ExportFileType.XLSX,
                report: ContractView.INTERNAL_GENERIC_REPORT,
              })
            }
          />

          <Divider orientation="vertical" />

          <ActionTextIcon
            icon="download"
            color="green"
            text={t("common.exportCsv")}
            onClick={() =>
              downloadContractsExport.mutate({
                ...validatedSearchParams,
                exportFileType: ExportFileType.CSV,
                report: ContractView.INTERNAL_GENERIC_REPORT,
              })
            }
          />
        </div>
      </ComponentWithPermission>

      <Table<ContractList>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: 1950 }}
        dataSource={contractListData?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(contractListData?.pageIndex) + 1,
          pageSize: contractListData?.pageSize,
          total: contractListData?.totalElementsCount,
        }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter, updateSearchParams)}
        showSorterTooltip={false}
      />

      <ContractChangeVerificationStatusForm
        open={!!contractForVerificationForm}
        contract={contractForVerificationForm}
        viewType="sendToBackoffice"
        onFormCancel={() => setContractForVerificationForm(undefined)}
      />
    </Card>
  );
};
